import React from "react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";

/**
 * Renders a truncated invoice description that expands into a hover card
 * listing every comma-separated item on its own line.
 *
 * Props:
 *  - description: full string (e.g. "Tomato, Onion, Milk, Bread, +19 more")
 *  - maxItemsPreview: how many items to show before "+N more" (default 2)
 *  - className: optional class overrides for the inline preview text
 *  - mobileMaxLen: optional max chars for tiny mobile rendering
 */
const DescriptionCell = ({ description, maxItemsPreview = 2, className = "", mobileMaxLen }) => {
  if (!description) return <p className={`text-xs text-stone-400 mt-0.5 truncate ${className}`}>-</p>;
  const items = description.split(",").map((s) => s.trim()).filter(Boolean);

  let preview = description;
  if (items.length > maxItemsPreview) {
    preview = `${items.slice(0, maxItemsPreview).join(", ")} +${items.length - maxItemsPreview} more`;
  }
  if (mobileMaxLen && preview.length > mobileMaxLen) preview = preview.substring(0, mobileMaxLen) + "...";

  const showHover = items.length > maxItemsPreview;

  if (!showHover) {
    return <p className={`text-xs text-stone-400 mt-0.5 truncate ${className}`}>{preview}</p>;
  }

  return (
    <HoverCard openDelay={150} closeDelay={80}>
      <HoverCardTrigger asChild>
        <button type="button" className={`text-left text-xs text-stone-400 mt-0.5 truncate hover:text-stone-600 hover:underline decoration-dotted underline-offset-2 transition-colors w-full ${className}`} data-testid="description-trigger">
          {preview}
        </button>
      </HoverCardTrigger>
      <HoverCardContent side="top" align="start" className="w-64 max-h-72 overflow-y-auto p-2.5 rounded-xl text-[10px] text-stone-700">
        <p className="text-[9px] font-bold uppercase tracking-widest text-stone-400 mb-1">All items ({items.length})</p>
        <ul className="space-y-0.5">
          {items.map((it, i) => (
            <li key={i} className="leading-snug text-[10px]">• {it}</li>
          ))}
        </ul>
      </HoverCardContent>
    </HoverCard>
  );
};

export default DescriptionCell;
